import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../services/api";

const QuestionsLayer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const quizId = queryParams.get("quizId");
  const [quizInfo, setQuizInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for categories and subcategories
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const fileInputRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editQuestionIndex, setEditQuestionIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [csvError, setCsvError] = useState(null);

  // Questions state
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  // New question form state
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    options: ["", "", "", ""],
    correctAnswer: 0,
    explanation: "",
    categoryId: "",
    subcategoryId: "",
    isActive: true,
  });

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.categories.getAll();
        if (response.success) {
          setCategories(response.data);
          if (response.data.length > 0) {
            setNewQuestion((prev) => ({
              ...prev,
              categoryId: response.data[0].id,
            }));
          }
        } else {
          toast.error("Failed to fetch categories");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching categories");
      }
    };

    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (newQuestion.categoryId) {
        try {
          const response = await api.categories.getSubcategories(
            newQuestion.categoryId
          );
          if (response.success) {
            setSubcategories(response.data);
            if (response.data.length > 0) {
              setNewQuestion((prev) => ({
                ...prev,
                subcategoryId: response.data[0].id,
              }));
            }
          } else {
            setSubcategories([]);
          }
        } catch (error) {
          console.error("Error fetching subcategories:", error);
          setSubcategories([]);
        }
      }
    };

    fetchSubcategories();
  }, [newQuestion.categoryId]);

  // Fetch quiz data based on quizId
  useEffect(() => {
    const fetchQuizInfo = async () => {
      if (quizId) {
        try {
          const response = await api.quizzes.getById(quizId);
          if (response.success) {
            setQuizInfo(response.data);
          } else {
            toast.error("Quiz not found");
            navigate("/quiz-mcqs");
          }
        } catch (error) {
          console.error("Error fetching quiz:", error);
          toast.error("Error fetching quiz");
          navigate("/quiz-mcqs");
        }
      }
    };

    fetchQuizInfo();
  }, [quizId, navigate]);

  // Function to fetch all questions (exam, quiz, and practice questions)
  const fetchAllQuestions = async () => {
    setLoading(true);
    try {
      // Fetch questions from different sources in parallel
      const [examQuestions, practiceQuestions, allQuizQuestions] = await Promise.all([
        api.exams.getAllQuestions(),
        api.practiceQuestions.getAll(),
        api.quizzes.getAllQuizQuestions(),
      ]);

      let combinedQuestions = [];

      // Add exam questions
      if (examQuestions.success) {
        const formattedExamQuestions = examQuestions.data.map((q) => ({
          ...q,
          source: "exam",
        }));
        combinedQuestions = [...combinedQuestions, ...formattedExamQuestions];
      }

      // Add practice questions
      if (practiceQuestions.success) {
        const formattedPracticeQuestions = practiceQuestions.data.map((q) => ({
          ...q,
          source: "practice",
        }));
        combinedQuestions = [
          ...combinedQuestions,
          ...formattedPracticeQuestions,
        ];
      }

      // Add all quiz questions (from question bank, not specific to any quiz)
      if (allQuizQuestions.success) {
        const formattedAllQuizQuestions = allQuizQuestions.data.map((q) => ({
          ...q,
          source: "quiz",
        }));
        combinedQuestions = [
          ...combinedQuestions,
          ...formattedAllQuizQuestions,
        ];
      }

      // Add quiz-specific questions if we're viewing a specific quiz
      if (quizId) {
        const quizQuestionsResponse = await api.quizzes.getQuizQuestions(
          quizId
        );
        if (quizQuestionsResponse.success) {
          const formattedQuizQuestions = quizQuestionsResponse.data.map(
            (item) => {
              const q = item.question || item;
              return {
                ...q,
                source: "quiz",
              };
            }
          );
          combinedQuestions = [...combinedQuestions, ...formattedQuizQuestions];
        }
      }

      setQuestions(combinedQuestions);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all questions on component mount
  useEffect(() => {
    fetchAllQuestions();
  }, [quizId]);

  // Filter questions when search term or selected category changes
  useEffect(() => {
    let filtered = [...questions];

    // Apply category filter
    if (selectedCategory !== "All") {
      filtered = filtered.filter((question) => {
        const category = categories.find((c) => c.id === question.categoryId);
        return category && category.name === selectedCategory;
      });
    }

    // Apply search filter
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((question) =>
        question.questionText?.toLowerCase().includes(term)
      );
    }

    setFilteredQuestions(filtered);
  }, [questions, selectedCategory, searchTerm, categories]);

  const handleDeleteQuestion = async (id, source) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        let response;

        if (source === "exam") {
          response = await api.exams.deleteQuestion(id);
        } else if (source === "practice") {
          response = await api.practiceQuestions.delete(id);
        } else if (source === "quiz" && quizId) {
          response = await api.quizzes.deleteQuestion(quizId, id);
        }

        if (response.success) {
          setQuestions(questions.filter((q) => q.id !== id));
          toast.success("Question deleted successfully");
        } else {
          toast.error("Failed to delete question");
        }
      } catch (error) {
        console.error("Error deleting question:", error);
        toast.error("Error deleting question");
      }
    }
  };

  const handleEditQuestion = (index) => {
    const question = filteredQuestions[index];
    setNewQuestion({
      id: question.id,
      questionText: question.questionText || question.question || "",
      options: question.options || [],
      correctAnswer: question.correctAnswer || 0,
      explanation: question.explanation || "",
      categoryId: question.categoryId || "",
      subcategoryId: question.subcategoryId || "",
      isActive: question.isActive !== false,
      source: question.source,
    });
    setEditQuestionIndex(index);
    setShowAddModal(true);
  };

  const handleQuestionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewQuestion({
      ...newQuestion,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...newQuestion.options];
    updatedOptions[index] = value;
    setNewQuestion({ ...newQuestion, options: updatedOptions });
  };

  const handleSaveQuestion = async () => {
    if (
      !newQuestion.questionText ||
      newQuestion.options.some((option) => !option) ||
      !newQuestion.categoryId
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      let response;
      // Format the question data according to what the API expects
      const questionData = {
        questionText: newQuestion.questionText,
        options: newQuestion.options,
        correctAnswer: Number(newQuestion.correctAnswer),
        explanation: newQuestion.explanation || "",
        categoryId: newQuestion.categoryId,
        subcategoryId: newQuestion.subcategoryId,
        isActive: newQuestion.isActive,
      };

      if (editQuestionIndex !== null) {
        // Update existing question
        const question = filteredQuestions[editQuestionIndex];

        if (question.source === "exam") {
          response = await api.exams.updateQuestion(question.id, questionData);
        } else if (question.source === "practice") {
          response = await api.practiceQuestions.update(
            question.id,
            questionData
          );
        } else if (question.source === "quiz" && quizId) {
          response = await api.quizzes.updateQuestion(
            quizId,
            question.id,
            questionData
          );
        }
      } else {
        // Create new question based on context
        if (quizId) {
          response = await api.quizzes.createQuestion(quizId, questionData);
        } else {
          // Default to practice question if not in quiz context
          response = await api.practiceQuestions.create(questionData);
        }
      }

      if (response.success) {
        toast.success(
          editQuestionIndex !== null
            ? "Question updated successfully"
            : "Question created successfully"
        );
        setShowAddModal(false);
        // Refresh questions
        fetchAllQuestions();
      } else {
        toast.error(response.message || "Failed to save question");
      }
    } catch (error) {
      console.error("Error saving question:", error);
      toast.error("Error saving question");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    setCsvError(null);

    if (!file) {
      return;
    }

    // Check file extension
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      setCsvError("Please upload a CSV file.");
      toast.error("Please upload a CSV file.");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const csvData = event.target.result;
        const lines = csvData.split("\n");

        // Filter out empty lines
        const nonEmptyLines = lines.filter((line) => line.trim() !== "");

        // Parse CSV lines
        const parsedQuestions = nonEmptyLines.map((line, index) => {
          const values = line.split(",").map((value) => value.trim());

          // Check if there are enough values
          if (values.length < 7) {
            throw new Error(
              `Line ${
                index + 1
              } does not have enough values. Format: question,option1,option2,option3,option4,correctOptionIndex,categoryName,subcategoryName,explanation`
            );
          }

          const [
            questionText,
            option1,
            option2,
            option3,
            option4,
            correctAnswerStr,
            categoryName,
            subcategoryName = "",
            explanation = "",
          ] = values;

          const correctAnswer = parseInt(correctAnswerStr, 10);

          // Validate correctAnswer
          if (isNaN(correctAnswer) || correctAnswer < 0 || correctAnswer > 3) {
            throw new Error(
              `Line ${
                index + 1
              } has an invalid correctOptionIndex. It should be a number between 0 and 3.`
            );
          }

          // Find category ID based on name
          const category = categories.find(
            (c) => c.name.toLowerCase() === categoryName.toLowerCase()
          );
          if (!category) {
            throw new Error(
              `Line ${index + 1} has an invalid category name: ${categoryName}`
            );
          }

          // Find subcategory ID if provided
          let subcategoryId = "";
          if (subcategoryName) {
            const matchingSubcategory = subcategories.find(
              (sc) =>
                sc.categoryId === category.id &&
                sc.name.toLowerCase() === subcategoryName.toLowerCase()
            );
            if (matchingSubcategory) {
              subcategoryId = matchingSubcategory.id;
            }
          }

          return {
            questionText,
            options: [option1, option2, option3, option4],
            correctAnswer,
            explanation,
            categoryId: category.id,
            subcategoryId,
            isActive: true,
          };
        });

        try {
          let response;
          if (quizId) {
            // Import to quiz
            response = await api.quizzes.bulkImportQuestions(
              quizId,
              parsedQuestions
            );
          } else {
            // Import as practice questions
            response = await api.practiceQuestions.bulkCreate(parsedQuestions);
          }

          if (response.success) {
            toast.success(
              `${parsedQuestions.length} questions imported successfully`
            );
            fetchAllQuestions(); // Refresh questions list
          } else {
            toast.error("Failed to import questions: " + response.message);
          }
        } catch (error) {
          console.error("Error importing questions:", error);
          toast.error("Error importing questions");
        }

        // Reset the file input
        e.target.value = null;
      } catch (error) {
        setCsvError(error.message);
        toast.error("Error parsing CSV: " + error.message);
        e.target.value = null;
      }
    };

    reader.onerror = () => {
      setCsvError("Failed to read the file. Please try again.");
      toast.error("Failed to read the file. Please try again.");
      e.target.value = null;
    };

    reader.readAsText(file);
  };

  const downloadSampleCsv = () => {
    const sampleContent = [
      "What is the proper way to store hazardous materials?,In any available container,In properly labeled sealed containers in designated areas,Near heat sources,Mixed with other chemicals,1,Environmental Care,Waste Management,Always store in designated areas with proper ventilation",
      "What should be your first action if you witness a workplace injury?,Call emergency services,Notify your supervisor,Ensure the area is safe and provide first aid if qualified,Document the incident,2,Health and Safety Measures,First Aid,Ensure safety first before providing assistance",
      "When working at heights what is a key safety measure?,Working alone to minimize distractions,Using improvised tools to save time,Inspecting equipment before use and using proper fall protection,Working during adverse weather conditions,2,Safe Working Practices,Working at Heights,Always inspect harnesses and safety equipment before use",
      "What are the main requirements of health and safety legislation?,To reduce workplace accidents,To provide financial compensation,To ensure that employers and employees follow safety regulations,To increase productivity,2,Laws and Regulations,Legal Framework,Legislation requires both employers and employees to comply with regulations",
      "What should be included in a risk assessment?,Only major hazards,All potential hazards and who might be harmed,Previous accident reports only,Just control measures,1,Hazards and Risks,Risk Assessment,Always document all potential hazards and affected parties",
    ].join("\n");

    const blob = new Blob([sampleContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_questions.csv");
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Sample CSV downloaded");
  };

  // Function to get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : "Unknown";
  };

  // Function to get subcategory name by ID
  const getSubcategoryName = (categoryId, subcategoryId) => {
    if (!subcategoryId) return "";
    const subcat = subcategories.find(
      (sc) => sc.id === subcategoryId && sc.categoryId === categoryId
    );
    return subcat ? subcat.name : "";
  };

  // Source badge color mapping
  const getSourceBadgeColor = (source) => {
    switch (source) {
      case "exam":
        return "bg-danger";
      case "quiz":
        return "bg-primary";
      case "practice":
        return "bg-success";
      default:
        return "bg-secondary";
    }
  };

  // Category badge color mapping
  const getCategoryBadgeColor = (categoryName) => {
    switch (categoryName) {
      case "Laws and Regulations":
        return "bg-primary";
      case "Hazards and Risks":
        return "bg-info";
      case "Accidents and Prevention":
        return "bg-danger";
      case "Safe Working Practices":
        return "bg-warning";
      case "Personal Protective Equipment":
        return "bg-secondary";
      case "Health and Safety Measures":
        return "bg-success";
      case "Fire Safety and Emergency Response":
        return "bg-danger-light";
      case "Environmental Care":
        return "bg-success-subtle";
      case "Communication and Safety Signs":
        return "bg-purple";
      default:
        return "bg-secondary";
    }
  };

  return (
    <div className="container-fluid">
      {quizInfo && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card bg-base">
              <div className="card-body p-16">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <h5 className="fw-semibold mb-1">{quizInfo.title}</h5>
                    <p className="text-muted mb-0">{quizInfo.description}</p>
                    <div className="mt-2">
                      <span className="badge bg-primary me-2">
                        {getCategoryName(quizInfo.categoryId)}
                      </span>
                      {quizInfo.subcategoryId && (
                        <span className="badge bg-secondary">
                          {getSubcategoryName(
                            quizInfo.categoryId,
                            quizInfo.subcategoryId
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-outline-primary mt-2 mt-md-0"
                    onClick={() => navigate("/quiz-mcqs")}
                  >
                    <Icon icon="mdi:arrow-left" className="me-1" />
                    Back to Quizzes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Question management header */}
      <div className="row mb-4">
        <div className="col-md-8">
          <div className="card bg-base">
            <div className="card-body p-16">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div>
                    <select
                      className="form-select"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="All">All Categories</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-4"
                      placeholder="Search questions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Icon
                      icon="heroicons:magnifying-glass"
                      className="position-absolute end-0 top-50 translate-middle-y me-2 text-secondary-light"
                      style={{ pointerEvents: "none" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-base">
            <div className="card-body p-16">
              <div className="d-flex justify-content-between align-items-center">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setEditQuestionIndex(null);
                    setNewQuestion({
                      questionText: "",
                      options: ["", "", "", ""],
                      correctAnswer: 0,
                      explanation: "",
                      categoryId: categories.length > 0 ? categories[0].id : "",
                      subcategoryId: "",
                      isActive: true,
                    });
                    setShowAddModal(true);
                  }}
                >
                  <Icon icon="mdi:plus" className="me-1" />
                  Add Question
                </button>
                <div className="dropdown">
                  <button
                    className="btn btn-outline-secondary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <Icon icon="mdi:file-import-outline" className="me-1" />
                    Import / Export
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button
                        className="dropdown-item"
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Icon
                          icon="mdi:file-upload"
                          className="me-2 text-primary"
                        />
                        Import CSV
                      </button>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                        ref={fileInputRef}
                      />
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        type="button"
                        onClick={downloadSampleCsv}
                      >
                        <Icon
                          icon="mdi:file-download"
                          className="me-2 text-success"
                        />
                        Download Sample CSV
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSV error display */}
      {csvError && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger">
              <h6 className="alert-heading">Error importing CSV</h6>
              <p className="mb-0">{csvError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Questions list */}
      <div className="row">
        <div className="col-12">
          <div className="card bg-base">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center flex-wrap">
              <h6 className="text-lg fw-semibold mb-0">Questions Bank</h6>
              <div className="d-flex align-items-center flex-wrap gap-2 mt-2 mt-md-0">
                {categories.slice(0, 4).map((category) => (
                  <div key={category.id} className="badge bg-primary me-2">
                    {category.name}:{" "}
                    {
                      questions.filter((q) => q.categoryId === category.id)
                        .length
                    }
                  </div>
                ))}
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "200px" }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="bg-neutral-50 dark:bg-neutral-900">
                      <tr>
                        <th scope="col" style={{ width: "5%" }}>
                          #
                        </th>
                        <th scope="col" style={{ width: "35%" }}>
                          Question
                        </th>
                        <th scope="col" style={{ width: "15%" }}>
                          Category
                        </th>
                        <th scope="col" style={{ width: "15%" }}>
                          Subcategory
                        </th>
                        <th scope="col" style={{ width: "10%" }}>
                          Source
                        </th>
                        <th scope="col" style={{ width: "20%" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQuestions.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-3">
                            No questions found matching your criteria.
                          </td>
                        </tr>
                      ) : (
                        filteredQuestions.map((question, index) => (
                          <tr key={question.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div
                                className="text-truncate"
                                style={{ maxWidth: "400px" }}
                              >
                                {question.questionText || question.question}
                              </div>
                              {question.explanation && (
                                <small className="text-muted d-block mt-1">
                                  <strong>Explanation:</strong>{" "}
                                  {question.explanation}
                                </small>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge ${getCategoryBadgeColor(
                                  getCategoryName(question.categoryId)
                                )}`}
                              >
                                {getCategoryName(question.categoryId)}
                              </span>
                            </td>
                            <td>
                              {question.subcategoryId && (
                                <span className="badge bg-secondary">
                                  {getSubcategoryName(
                                    question.categoryId,
                                    question.subcategoryId
                                  )}
                                </span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge ${getSourceBadgeColor(
                                  question.source
                                )}`}
                              >
                                {question.source === "exam"
                                  ? "Exam"
                                  : question.source === "quiz"
                                  ? "Quiz"
                                  : "Practice"}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info me-2"
                                onClick={() => handleEditQuestion(index)}
                              >
                                <Icon icon="tabler:edit" className="icon" />
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() =>
                                  handleDeleteQuestion(
                                    question.id,
                                    question.source
                                  )
                                }
                              >
                                <Icon icon="tabler:trash" className="icon" />
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Question Modal */}
      {showAddModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content bg-base">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editQuestionIndex !== null
                    ? "Edit Question"
                    : "Add New Question"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Question</label>
                    <textarea
                      className="form-control"
                      name="questionText"
                      value={newQuestion.questionText}
                      onChange={handleQuestionChange}
                      rows="2"
                      required
                    ></textarea>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Category</label>
                      <select
                        className="form-select"
                        name="categoryId"
                        value={newQuestion.categoryId}
                        onChange={handleQuestionChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Subcategory
                      </label>
                      <select
                        className="form-select"
                        name="subcategoryId"
                        value={newQuestion.subcategoryId}
                        onChange={handleQuestionChange}
                      >
                        <option value="">Select Subcategory</option>
                        {subcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Explanation (Optional)
                    </label>
                    <textarea
                      className="form-control"
                      name="explanation"
                      value={newQuestion.explanation}
                      onChange={handleQuestionChange}
                      rows="2"
                      placeholder="Provide an explanation for the correct answer"
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Options</label>
                    {newQuestion.options.map((option, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2"
                      >
                        <div className="form-check style-check me-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="correctAnswer"
                            checked={
                              Number(newQuestion.correctAnswer) === index
                            }
                            onChange={() =>
                              setNewQuestion({
                                ...newQuestion,
                                correctAnswer: index,
                              })
                            }
                            required
                          />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          value={option}
                          onChange={(e) =>
                            handleOptionChange(index, e.target.value)
                          }
                          placeholder={`Option ${index + 1}`}
                          required
                        />
                      </div>
                    ))}
                    <small className="text-muted">
                      Select the radio button next to the correct answer.
                    </small>
                  </div>

                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="isActive"
                      id="questionActiveStatus"
                      checked={newQuestion.isActive}
                      onChange={handleQuestionChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="questionActiveStatus"
                    >
                      Question is active and available
                    </label>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveQuestion}
                >
                  Save Question
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionsLayer;
